// src/pages/returnBatch/ReturnBatchListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Eye,
  Download,
  XCircle,
  Truck,
  Calendar,
  Clock,
  CheckCircle
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import {
  getAllReturnBatches,
  getUserWarehouses,
  closeReturnBatch
} from '../../services/api/returnBatch';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { getAllCourierCompanies } from '../../services/api/courierCompany';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatTime } from '../../utils/dateUtils';
import styles from './ReturnBatchListPage.module.css';

const ReturnBatchListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // State management
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState({});

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    warehouseId: '',
    courierCompanyId: '',
    businessCourierId: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Reference data for filters
  const [referenceData, setReferenceData] = useState({
    warehouses: [],
    courierCompanies: [],
    courierAccounts: []
  });

  // Bulk actions
  const [selectedBatches, setSelectedBatches] = useState([]);

  // Status configuration
  const statusConfig = {
    active: {
      label: t('returnBatch.statusActive'),
      color: '#10b981',
      bgColor: '#d1fae5',
      icon: <Clock size={14} />
    },
    closed: {
      label: t('returnBatch.statusClosed'),
      color: '#6b7280',
      bgColor: '#f3f4f6',
      icon: <CheckCircle size={14} />
    }
  };

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Load batches when filters or pagination/sorting changes
  useEffect(() => {
    fetchBatches();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const loadReferenceData = async () => {
    try {
      const [warehousesData, couriersData, companiesData] = await Promise.all([
        getUserWarehouses(),
        getBusinessCouriers(),
        getAllCourierCompanies()
      ]);
      
      setReferenceData({
        warehouses: warehousesData.warehouses || [],
        courierAccounts: couriersData.couriers || [],
        courierCompanies: companiesData.courierCompanies || []
      });
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const fetchBatches = async () => {
    try {
      setIsLoading(true);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      // Add filters
      if (filters.search) params.search = filters.search;
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.courierCompanyId) params.courierCompanyId = filters.courierCompanyId;
      if (filters.businessCourierId) params.businessCourierId = filters.businessCourierId;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await getAllReturnBatches(params);
      setBatches(response.batches || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));

    } catch (err) {
      console.error('Failed to fetch batches:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('returnBatch.failedToLoadBatches'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      warehouseId: '',
      courierCompanyId: '',
      businessCourierId: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  const handleCloseBatch = async (batchId, batchNumber) => {
    if (!window.confirm(t('returnBatch.confirmCloseBatch', { batchNumber }))) {
      return;
    }

    setIsClosing(prev => ({ ...prev, [batchId]: true }));
    try {
      await closeReturnBatch(batchId);
      success(t('returnBatch.batchClosedSuccessfully'));
      fetchBatches();
    } catch (err) {
      console.error('Close batch failed:', err);
      showError(err.response?.data?.message || t('returnBatch.failedToCloseBatch'));
    } finally {
      setIsClosing(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const handleBulkClose = async () => {
    const activeBatches = selectedBatches.filter(id => {
      const batch = batches.find(b => b.id === id);
      return batch && batch.status === 'active';
    });

    if (activeBatches.length === 0) {
      showError(t('returnBatch.noActiveBatchesSelected'));
      return;
    }

    if (!window.confirm(t('returnBatch.confirmBulkClose', { count: activeBatches.length }))) {
      return;
    }

    try {
      await Promise.all(activeBatches.map(id => closeReturnBatch(id)));
      success(t('returnBatch.bulkClosedSuccessfully', { count: activeBatches.length }));
      setSelectedBatches([]);
      fetchBatches();
    } catch (err) {
      console.error('Bulk close failed:', err);
      showError(t('returnBatch.failedToCloseSomeBatches'));
    }
  };

  const handleExport = () => {
    // Create CSV content
    const headers = [
      'Batch Number',
      'Courier Company',
      'Courier Account',
      'Warehouse',
      'Total Orders',
      'Total Value',
      'Status',
      'Created Date',
      'Created By'
    ];
    
    const rows = batches.map(batch => [
      batch.batchNumber,
      batch.CourierCompany?.name || 'N/A',
      batch.courierAccount?.accountName || 'N/A',
      batch.Warehouse?.name || 'N/A',
      batch.totalOrders,
      batch.totalValue,
      statusConfig[batch.status]?.label || batch.status,
      formatDate(batch.createdAt),
      batch.createdBy?.name || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `return-batches-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getFilteredCourierAccounts = () => {
    if (!filters.courierCompanyId) {
      return referenceData.courierAccounts;
    }
    return referenceData.courierAccounts.filter(
      account => account.courierCompanyId === parseInt(filters.courierCompanyId)
    );
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('returnBatch.warehouse'),
      type: 'select',
      icon: <Truck size={18} />,
      placeholder: t('returnBatch.allWarehouses'),
      options: referenceData.warehouses.map(w => ({
        value: w.id.toString(),
        label: w.name
      }))
    },
    {
      key: 'courierCompanyId',
      label: t('returnBatch.courierCompany'),
      type: 'select',
      placeholder: t('returnBatch.allCourierCompanies'),
      options: referenceData.courierCompanies.map(c => ({
        value: c.id.toString(),
        label: c.name
      }))
    },
    {
      key: 'businessCourierId',
      label: t('returnBatch.courierAccount'),
      type: 'select',
      placeholder: t('returnBatch.allCourierAccounts'),
      options: getFilteredCourierAccounts().map(a => ({
        value: a.id.toString(),
        label: a.accountName
      })),
      disabled: !filters.courierCompanyId && getFilteredCourierAccounts().length === 0
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: t('returnBatch.allStatuses'),
      options: Object.entries(statusConfig).map(([key, config]) => ({
        value: key,
        label: config.label
      }))
    },
    {
      key: 'date',
      label: t('returnBatch.dateRange'),
      type: 'daterange',
      gridColumn: 'span 2'
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'batchNumber',
      header: t('returnBatch.batchNumber'),
      sortable: true,
      width: '150px',
      render: (row) => (
        <div className={styles.batchNumber}>{row.batchNumber}</div>
      )
    },
    {
      key: 'courierAccount',
      header: t('returnBatch.courier'),
      width: '180px',
      render: (row) => (
        <div className={styles.courierBadge}>
          <Truck size={12} />
          {row.courierAccount?.accountName || t('common.na')}
        </div>
      )
    },
    {
      key: 'totalOrders',
      header: t('returnBatch.orders'),
      sortable: true,
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className={styles.ordersCount}>
          <Package size={12} />
          {row.totalOrders}
        </div>
      )
    },
    {
      key: 'warehouse',
      header: t('returnBatch.warehouse'),
      width: '150px',
      render: (row) => (
        <div className={styles.warehouseBadge}>
          {row.Warehouse?.name || t('common.na')}
        </div>
      )
    },
    {
      key: 'totalValue',
      header: t('returnBatch.totalValue'),
      sortable: true,
      width: '130px',
      align: 'right',
      render: (row) => (
        <div className={styles.totalValue}>
          {formatCurrency(row.totalValue)}
        </div>
      )
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '120px',
      render: (row) => {
        const statusInfo = statusConfig[row.status] || statusConfig.active;
        return (
          <div 
            className={styles.statusBadge}
            style={{
              backgroundColor: statusInfo.bgColor,
              borderColor: statusInfo.color,
              color: statusInfo.color
            }}
          >
            {statusInfo.icon}
            <span>{statusInfo.label}</span>
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      sortable: true,
      width: '160px',
      render: (row) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}>
            <Calendar size={12} />
            {formatDate(row.createdAt)}
          </div>
          <div className={styles.timeInfo}>
            {formatTime(row.createdAt)}
          </div>
        </div>
      )
    },
    {
      key: 'createdBy',
      header: t('common.createdBy'),
      width: '150px',
      render: (row) => (
        <div className={styles.createdByInfo}>
          {row.createdBy?.name || t('common.na')}
        </div>
      )
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        <Link
          to={`/return-batches/${row.id}`}
          className={`${styles.actionButton} ${styles.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={16} />
        </Link>

        {row.status === 'active' && hasPermission('process_returns') && (
          <button
            onClick={() => handleCloseBatch(row.id, row.batchNumber)}
            disabled={isClosing[row.id]}
            className={`${styles.actionButton} ${styles.closeButton}`}
            title={t('returnBatch.closeBatch')}
          >
            {isClosing[row.id] ? (
              <div className={styles.smallSpinner}></div>
            ) : (
              <XCircle size={16} />
            )}
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            // Export single batch logic here if needed
          }}
          className={`${styles.actionButton} ${styles.exportButton}`}
          title={t('common.export')}
        >
          <Download size={16} />
        </button>
      </div>
    ),
    width: '140px',
    frozen: true
  };

  return (
    <div className={styles.returnBatchListPage}>
      {/* Page Header */}
      <PageHeader
        title={t('returnBatch.allReturnBatches')}
        description={t('returnBatch.viewAndManageAllReturnBatches')}
        icon={Package}
        actions={
          hasPermission('process_returns') && (
            <Link to="/return-batches/process" className={styles.primaryButton}>
              <Plus size={20} />
              {t('returnBatch.processReturns')}
            </Link>
          )
        }
      />

      {/* Bulk Actions Bar (shown when items selected) */}
      {selectedBatches.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <span className={styles.bulkActionsText}>
            {selectedBatches.length} {t('common.selected')}
          </span>
          <button
            onClick={handleBulkClose}
            className={styles.bulkActionButton}
          >
            <XCircle size={18} />
            {t('returnBatch.closeSelected', { count: selectedBatches.length })}
          </button>
        </div>
      )}

      {/* Search and Filters Row */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('returnBatch.searchByBatchNumber')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={batches}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedBatches}
        onSelectionChange={setSelectedBatches}
        onRowClick={(row) => navigate(`/return-batches/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() 
            ? t('returnBatch.noBatchesFound')
            : t('returnBatch.noReturnBatchesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('returnBatch.noBatchesFoundDescription')
            : t('returnBatch.noReturnBatchesYetDescription')
        }
        emptyIcon={<Package size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={() => {
              handleClearFilters();
            }} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('process_returns') ? (
            <Link to="/return-batches/process" className={styles.primaryButton}>
              <Plus size={18} />
              {t('returnBatch.processFirstReturn')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default ReturnBatchListPage;